// Icon.tsx
// Icons import
import Alert from "./alert.svg";
import Add from "./add.svg";
import Minus from "./minus.svg";
import Cancel from "./cancel.svg";
import CancelCircle from "./cancel-circle.svg";
import Resolved from "./resolved.svg";
import RadioButtonChecked from "./radio-button-checked.svg";
import RadioButtonUnchecked from "./radio-button-unchecked.svg";
import FallbackImage from "./fallback-image.svg";
import CheckCircle from "./check-circle.svg";
import CheckCircleExtra from "./check-circle-extra.svg";
import Loader from "./loader.svg";
import EyeOpen from "./eye-open.svg";
import EyeClosed from "./eye-closed.svg";
import ChevronDown from "./chevron-down.svg";
import Upload from "./upload.svg";
import Download from "./download.svg";
import Document from "./document.svg";
import Trash from "./trash.svg";
import NotFound from "./notfound.svg";
import AlertTriangle from "./alert-triangle.svg";
import ChevronRight from "./chevron-right.svg";
import ChevronLeft from "./chevron-left.svg";
import ArrowLeft from "./arrow-left.svg";
import Calendar from "./calendar.svg";
import CheckboxChecked from "./checkbox-checked.svg";
import CheckboxUnchecked from "./checkbox-unchecked.svg";
import Cookies from "./cookies.svg";
import Check from "./check.svg";
import CookiesPreferences from "./cookies-preferences.svg";
import Home from "./home.svg";
import Orders from "./orders.svg";
import AndercoreLogoBig from "./andercore-logo-big.svg";
import AndercoreLogoSmall from "./andercore-logo-small.svg";
import ChevronUp from "./chevron-up.svg";
import Modules from "./modules.svg";
import EN from "./en.svg";
import DE from "./de.svg";
import FR from "./fr.svg";
import ES from "./es.svg";
import BE from "./be.svg";
import NL from "./nl.svg";
import PT from "./pt.svg";
import AT from "./at.svg";
import PL from "./pl.svg";
import CH from "./sw.svg";
import CZ from "./cz.svg";
import Container from "./container.svg";
import Pieces from "./pieces.svg";
import Pallets from "./pallets.svg";
import CalendarTime from "./calendar-time.svg";
import Comment from "./comment.svg";
import InvoiceDue from "./invoice-due.svg";
import Invoices from "./invoices.svg";
import Chart from "./chart.svg";
import Mail from "./mail.svg";
import QuestionMarkCircle from "./question-mark-circle.svg";
import LinkedIn from "./linkedin.svg";
import Link from "./link.svg";
import Refresh from "./refresh.svg";
import FileUpload from "./FileUpload.svg";
import Logout from "./logout.svg";
import Filter from "./filter.svg";
import DeliveryInProgress from "./delivery-in-progress.svg";
import Flame from "./flame.svg";
import Billing from "./billing.svg";
import Warehouse from "./warehouse.svg";
import ArrowUp from "./arrow-up.svg";
import ArrowDown from "./arrow-down.svg";
import Checklist from "./checklist.svg";

// External imports
import { Suspense } from "react";
import { twMerge } from "tailwind-merge";
import { ColorModeEnum } from "~/utils/generalUtils";
import { colors } from "~/utils/tailwindUtils";
import Flex from "../flex/Flex";

interface IconType {
  [key: string]: React.SVGProps<SVGSVGElement>;
}

/**
 * IconName enum to define all the icons used in the application
 */
export enum IconName {
  CheckCircle = "check-circle",
  CheckCircleExtra = "check-circle-extra",
  Alert = "alert",
  Add = "add",
  Minus = "minus",
  Cancel = "cancel",
  CancelCircle = "cancel-circle",
  Resolved = "resolved",
  RadioButtonChecked = "radio-button-checked",
  RadioButtonUnchecked = "radio-button-unchecked",
  FallbackImage = "fallback-image",
  Loader = "loader",
  EyeOpen = "eye-open",
  EyeClosed = "eye-closed",
  ChevronDown = "chevron-down",
  Upload = "upload",
  Download = "download",
  Document = "document",
  Trash = "trash",
  NotFound = "notfound",
  AlertTriangle = "alert-triangle",
  ChevronUp = "chevron-up",
  ChevronRight = "chevron-right",
  ChevronLeft = "chevron-left",
  ArrowLeft = "arrow-left",
  Calendar = "calendar",
  CheckboxChecked = "checkbox-checked",
  CheckboxUnchecked = "checkbox-unchecked",
  Cookies = "cookies",
  Check = "check",
  CookiesPreferences = "cookies-preferences",
  Home = "home",
  Orders = "orders",
  AndercoreLogoBig = "andercore-logo-big",
  AndercoreLogoSmall = "andercore-logo-small",
  Modules = "modules",
  EN = "en",
  DE = "de",
  FR = "fr",
  ES = "es",
  BE = "be",
  NL = "nl",
  PT = "pt",
  AT = "at",
  PL = "pl",
  CH = "sw",
  CZ = "cz",
  Container = "container",
  Pieces = "pieces",
  Pallets = "pallets",
  CalendarTime = "calendar-time",
  Comment = "comment",
  InvoiceDue = "invoice-due",
  Invoices = "invoices",
  Chart = "chart",
  DeliveryInProgress = "delivery-in-progress",
  Mail = "mail",
  QuestionMarkCircle = "question-mark-circle",
  LinkedIn = "linkedin",
  Link = "link",
  Refresh = "refresh",
  FileUpload = "file-upload",
  Logout = "logout",
  Filter = "filter",
  Flame = "flame",
  Billing = "billing",
  Warehouse = "warehouse",
  ArrowUp = "arrow-up",
  ArrowDown = "arrow-down",
  Checklist = "checklist",
}

const iconType: IconType = {
  [IconName.Alert]: Alert,
  [IconName.Add]: Add,
  [IconName.Minus]: Minus,
  [IconName.Cancel]: Cancel,
  [IconName.CancelCircle]: CancelCircle,
  [IconName.Resolved]: Resolved,
  [IconName.RadioButtonChecked]: RadioButtonChecked,
  [IconName.RadioButtonUnchecked]: RadioButtonUnchecked,
  [IconName.FallbackImage]: FallbackImage,
  [IconName.CheckCircle]: CheckCircle,
  [IconName.CheckCircleExtra]: CheckCircleExtra,
  [IconName.Loader]: Loader,
  [IconName.EyeOpen]: EyeOpen,
  [IconName.EyeClosed]: EyeClosed,
  [IconName.ChevronDown]: ChevronDown,
  [IconName.Upload]: Upload,
  [IconName.Download]: Download,
  [IconName.Document]: Document,
  [IconName.Trash]: Trash,
  [IconName.NotFound]: NotFound,
  [IconName.AlertTriangle]: AlertTriangle,
  [IconName.ChevronRight]: ChevronRight,
  [IconName.ChevronLeft]: ChevronLeft,
  [IconName.ArrowLeft]: ArrowLeft,
  [IconName.ChevronUp]: ChevronUp,
  [IconName.Calendar]: Calendar,
  [IconName.CheckboxChecked]: CheckboxChecked,
  [IconName.CheckboxUnchecked]: CheckboxUnchecked,
  [IconName.Cookies]: Cookies,
  [IconName.Check]: Check,
  [IconName.CookiesPreferences]: CookiesPreferences,
  [IconName.Home]: Home,
  [IconName.Orders]: Orders,
  [IconName.AndercoreLogoBig]: AndercoreLogoBig,
  [IconName.AndercoreLogoSmall]: AndercoreLogoSmall,
  [IconName.Modules]: Modules,
  [IconName.EN]: EN,
  [IconName.DE]: DE,
  [IconName.FR]: FR,
  [IconName.ES]: ES,
  [IconName.BE]: BE,
  [IconName.NL]: NL,
  [IconName.PT]: PT,
  [IconName.AT]: AT,
  [IconName.PL]: PL,
  [IconName.CH]: CH,
  [IconName.CZ]: CZ,
  [IconName.Container]: Container,
  [IconName.Pieces]: Pieces,
  [IconName.Pallets]: Pallets,
  [IconName.CalendarTime]: CalendarTime,
  [IconName.Comment]: Comment,
  [IconName.InvoiceDue]: InvoiceDue,
  [IconName.Invoices]: Invoices,
  [IconName.DeliveryInProgress]: DeliveryInProgress,
  [IconName.Chart]: Chart,
  [IconName.Mail]: Mail,
  [IconName.QuestionMarkCircle]: QuestionMarkCircle,
  [IconName.LinkedIn]: LinkedIn,
  [IconName.Link]: Link,
  [IconName.Refresh]: Refresh,
  [IconName.FileUpload]: FileUpload,
  [IconName.Logout]: Logout,
  [IconName.Filter]: Filter,
  [IconName.Flame]: Flame,
  [IconName.Billing]: Billing,
  [IconName.Warehouse]: Warehouse,
  [IconName.ArrowUp]: ArrowUp,
  [IconName.ArrowDown]: ArrowDown,
  [IconName.Checklist]: Checklist,
};

export interface IconProps extends React.HTMLAttributes<HTMLDivElement> {
  name: string;
  size?: number;
  width?: number;
  height?: number;
  hovered?: boolean;
  color?: string | undefined;
  colorMode?: "fill" | "stroke";
  customStrokeWidth?: number;
  style?: React.CSSProperties;
}

/**
 * Icon component to render SVG icons
 * - name: Ex: IconName.Home
 * - size: 24 default
 * - width: default undefined so size will be added to width, if defined it will override size
 * - height: default undefined so size will be added to height, if defined it will override size
 * - hovered: boolean to change color on hover
 * - color: default #3B3F44
 * - colorMode: 'fill' | 'stroke' default 'stroke'
 * - customStrokeWidth: number to override default stroke width of 1
 * - className: string to add custom classes to Flex component surrounding the icon
 * - ...restProps: any other props to be passed to the Flex component
 */
export const Icon = ({
  name,
  size = 24,
  width,
  height,
  style,
  hovered,
  color = colors.primary as unknown as string,
  colorMode = ColorModeEnum.Stroke,
  customStrokeWidth,
  className,
  ...restProps
}: IconProps) => {
  const IconComponent = (iconType[name] || Alert) as React.FC<React.SVGProps<SVGSVGElement>>;

  const strokeWidth = colorMode === ColorModeEnum.Stroke ? 1 : 0;
  const fill = colorMode === ColorModeEnum.Fill ? color : "none";

  return (
    <Flex className={twMerge("justify-center items-center", className)} {...restProps}>
      <Suspense fallback={<></>}>
        <IconComponent
          data-testid="icon"
          stroke={hovered ? colors.red[600] : color}
          height={height ?? size}
          width={width ?? size}
          strokeWidth={customStrokeWidth || strokeWidth}
          fill={fill}
          {...(style && { style })}
        />
      </Suspense>
    </Flex>
  );
};
